import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './linenHire.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCircle, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { toDubai, dubaiDate } from '../../../utils/dubaiTime';

const LinenHire = () => {
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({ total_single_sets: 0, total_double_sets: 0, total_super_king_sets: 0 });
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchProperties = async () => {
            try {
                const response = await axios.get('/api/linen-hire/properties');
                setProperties(response.data);
            } catch (error) {
                console.error('Error fetching linen hire properties:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProperties();
    }, []);

    const fetchSummary = async () => {
        try {
            const response = await axios.get('/api/linen-hire/summary/active-sets');
            setSummary(response.data);
        } catch (error) {
            console.error('Error fetching linen hire summary:', error);
        }
    };

    fetchSummary();

    // Asia/Dubai render (item 8) — same dd/mm/yyyy visual as before.
    const formatDate = (date) => (date ? dubaiDate(date) || 'N/A' : 'N/A');

    const isOverdue = (overdueDate) => {
        if (!overdueDate) return false;
        return new Date(overdueDate) < new Date();
    };

    const formatPropertyId = (id) => `#${id.toString().padStart(3, '0')}`;

    const truncateText = (text, maxLength = 12) => {
        if (!text) return '';
        return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
    };

    const toggleReturned = async (propertyId, currentStatus) => {
        const confirmMsg = currentStatus
            ? 'Are you sure you want to mark this as active?'
            : 'Are you sure you want to mark this as returned?';

        if (!window.confirm(confirmMsg)) return;

        try {
            const response = await axios.patch(`/api/linen-hire/toggle-returned/${propertyId}`);
            const updatedProperty = response.data.property;

            setProperties(prev =>
                prev.map(p =>
                    p.property_id === updatedProperty.id
                        ? { ...p, is_lh_returned: updatedProperty.is_lh_returned }
                        : p
                )
            );
        } catch (error) {
            console.error('Error toggling is_lh_returned:', error);
        }
    };

    const calculateCycleInfo = (lastBookingDate) => {
        if (!lastBookingDate) return { cycles: 0, expiry: 'N/A', expiryDateObject: null };

        const now = new Date();
        const lastDate = new Date(lastBookingDate);
        const diffInMs = now - lastDate;
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        const cycles = Math.floor(diffInDays / 14);

        if (cycles < 1) return { cycles: 0, expiry: 'N/A', expiryDateObject: null };

        // Day arithmetic anchored in Dubai (item 8) — device-local setDate()
        // could land a day off across the device's DST changes.
        const expiryDubai = toDubai(lastDate).plus({ days: cycles * 14 });

        return {
            cycles,
            expiry: formatDate(expiryDubai.toJSDate()),
            expiryDateObject: expiryDubai.toJSDate()
        };
    };

    if (loading) return <p>Loading...</p>;
    const filteredProperties = properties.filter((property) => {
        const search = searchTerm.toLowerCase();
        return (
            formatPropertyId(property.property_id).toLowerCase().includes(search) ||
            property.client_name?.toLowerCase().includes(search) ||
            property.building_name?.toLowerCase().includes(search) ||
            property.unit_number?.toLowerCase().includes(search)
        );
    });

    const sortedProperties = [...filteredProperties].sort((a, b) => {
        // Step 1: Move returned items to the bottom
        if (a.is_lh_returned && !b.is_lh_returned) return 1;
        if (!a.is_lh_returned && b.is_lh_returned) return -1;

        // Step 2: Get expiry and booking dates
        const aInfo = calculateCycleInfo(a.last_booking_date);
        const bInfo = calculateCycleInfo(b.last_booking_date);

        const expiryA = aInfo.expiryDateObject;
        const expiryB = bInfo.expiryDateObject;

        // Step 3: If both have expiry → sort by expiry DESC
        if (expiryA && expiryB) return expiryB - expiryA;

        // Step 4: If one has expiry and the other doesn't → expiry first
        if (expiryA && !expiryB) return -1;
        if (!expiryA && expiryB) return 1;

        // Step 5: If both don't have expiry → sort by last_booking_date ASC
        const dateA = a.last_booking_date ? new Date(a.last_booking_date) : new Date(8640000000000000); // far future
        const dateB = b.last_booking_date ? new Date(b.last_booking_date) : new Date(8640000000000000);
        return dateA - dateB;
    });


    return (
        <div className="linenhire-container">
            <h2>Linen Hire Properties</h2>
            <div className="search-bar-wrapper">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                    type="text"
                    placeholder="Search by Client, Building Name, Unit Number, or Property ID..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="search-bar"
                />
            </div>
            <div className="linenhire-summary">
                <strong>Total Active Sets:</strong>{' '}
                {summary.total_single_sets} Single + {summary.total_double_sets} Double{Number(summary.total_super_king_sets) > 0 ? ` + ${summary.total_super_king_sets} Super King` : ''}
            </div>
            {sortedProperties.length === 0 ? (
                <p>No properties with linen hire found.</p>
            ) : (
                <table className="linenhire-table">
                    <thead>
                        <tr>
                            <th>P. ID</th>
                            <th>Client Name</th>
                            <th>Building Name</th>
                            <th style={{ textAlign: 'center' }}>Unit</th>
                            <th style={{ textAlign: 'center' }}>Single</th>
                            <th style={{ textAlign: 'center' }}>Double</th>
                            <th style={{ textAlign: 'center' }}>S. King</th>
                            <th style={{ textAlign: 'center' }}>Last</th>
                            <th style={{ textAlign: 'center' }}>Cycle</th>
                            <th style={{ textAlign: 'center' }}>Expiry</th>
                            <th style={{ textAlign: 'center' }}>Picked</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedProperties.map((property) => {
                            const overdue = isOverdue(property.overdue_date);
                            const isReturned = property.is_lh_returned;

                            return (
                                <tr
                                    key={property.property_id}
                                    className={`linenhire-row ${isReturned ? 'returned-row' : ''}`}
                                    style={{
                                        backgroundColor: overdue ? '#FFCCCC' : 'transparent',
                                    }}
                                >
                                    <td>
                                        <Link to={`/properties/${property.property_id}`} className="clickable-link">
                                            {formatPropertyId(property.property_id)}
                                        </Link>
                                    </td>
                                    <td>{truncateText(property.client_name)}</td>
                                    <td>{truncateText(property.building_name)}</td>
                                    <td style={{ textAlign: 'center' }}>{property.unit_number}</td>
                                    <td style={{ textAlign: 'center' }}>{property.single_beds}</td>
                                    <td style={{ textAlign: 'center' }}>{property.double_beds}</td>
                                    <td style={{ textAlign: 'center' }}>{property.super_king_beds || 0}</td>
                                    <td style={{ textAlign: 'center' }}>
                                        {property.last_booking_id ? (
                                            <Link to={`/bookings/${property.last_booking_id}`} className="clickable-link">
                                                {formatDate(property.last_booking_date)}
                                            </Link>
                                        ) : (
                                            formatDate(property.last_booking_date)
                                        )}
                                    </td>
                                    {(() => {
                                        const { cycles, expiry } = calculateCycleInfo(property.last_booking_date);
                                        return (
                                            <>
                                                <td style={{ textAlign: 'center' }}>{cycles}</td>
                                                <td style={{ textAlign: 'center' }}>{expiry}</td>
                                            </>
                                        );
                                    })()}
                                    <td
                                        style={{ textAlign: 'center', cursor: 'pointer' }}
                                        onClick={() => toggleReturned(property.property_id, isReturned)}
                                        title="Click to toggle returned status"
                                    >
                                        <FontAwesomeIcon
                                            icon={isReturned ? faCheckCircle : faCircle}
                                            className="return-icon"
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default LinenHire;
